import axios, {
  AxiosHeaders,
  type AxiosRequestConfig,
  type AxiosRequestTransformer,
  type AxiosResponse,
  type AxiosResponseTransformer,
  isAxiosError,
  isCancel,
  type RawAxiosHeaders,
} from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { stringify } from 'qs'

import { metaContent } from '@/lib/utils'
import { type ApiErrorBody, ApiServiceError } from '@/types/api'

type UnauthorizedHandler = (error: ApiServiceError) => void

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.'
const SESSION_ENDPOINT = /\/api\/v1\/session(?:[/?#]|$)/

// Only for responses without the API error envelope (a proxy error page, a dropped connection); the
// names and messages match what the Rails rescuer sends.
const STATUS_ERROR_TYPES: Record<number, string> = {
  400: 'invalid_request',
  401: 'authentication_required',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  422: 'validation_error',
  429: 'rate_limited',
  503: 'service_unavailable',
}

const STATUS_MESSAGES: Record<number, string> = {
  0: 'Could not reach the server. Check your connection and try again.',
  401: 'You need to sign in to continue.',
  403: 'You are not allowed to do that.',
  404: 'Not found.',
  429: 'Too many attempts. Please wait a few minutes and try again.',
}

const unauthorizedHandlers = new Set<UnauthorizedHandler>()
let rotatedCsrfToken: string | undefined

export function getCsrfToken(): string | undefined {
  return metaContent('csrf-token') ?? rotatedCsrfToken
}

// Signing in or out rotates the CSRF token, and the API returns the current one on every response
// (errors included). Without keeping it, the next write fails with 422 invalid_authenticity_token.
const installCsrfToken = (headers: AxiosResponse['headers'] | undefined) => {
  const token = headers && AxiosHeaders.from(headers as RawAxiosHeaders).get('X-CSRF-Token')
  if (typeof token !== 'string' || token === '') return
  rotatedCsrfToken = token
  document.querySelector('meta[name="csrf-token"]')?.setAttribute('content', token)
}

// Only plain objects and arrays are re-keyed: humps would flatten FormData, Blob, or File into {}.
const isJsonLike = (data: unknown): data is object =>
  Array.isArray(data) || Object.prototype.toString.call(data) === '[object Object]'

const transformRequest: AxiosRequestTransformer = (data, headers) => {
  const token = getCsrfToken()
  if (token) headers.set('X-CSRF-Token', token)

  if (data instanceof FormData) {
    // With a JSON content type, axios would serialize the FormData into a JSON string.
    headers.delete('Content-Type')
    return data
  }
  return isJsonLike(data) ? decamelizeKeys(data) : data
}

const transformResponse: AxiosResponseTransformer = (data) =>
  isJsonLike(data) ? camelizeKeys(data) : data

const readErrorBody = (data: unknown): ApiErrorBody => {
  if (!isJsonLike(data)) return {}
  const { error, errorType, errors } = data as Record<string, unknown>
  return {
    error: typeof error === 'string' && error !== '' ? error : undefined,
    errorType: typeof errorType === 'string' ? errorType : undefined,
    errors: Array.isArray(errors)
      ? errors.filter((item): item is string => typeof item === 'string')
      : undefined,
  }
}

const defaultErrorType = (status: number): string => {
  if (status === 0) return 'network_error'
  return STATUS_ERROR_TYPES[status] ?? (status >= 500 ? 'internal_error' : 'request_failed')
}

const toApiServiceError = (error: unknown): ApiServiceError => {
  if (error instanceof ApiServiceError) return error
  if (isCancel(error)) {
    return new ApiServiceError({
      status: 0,
      errorType: 'cancelled',
      message: 'The request was canceled.',
    })
  }
  if (!isAxiosError(error)) {
    return new ApiServiceError({
      status: 0,
      errorType: 'unknown_error',
      message: DEFAULT_MESSAGE,
      data: error,
    })
  }

  const status = error.response?.status ?? 0
  const data: unknown = error.response?.data
  const body = readErrorBody(data)
  const errors = body.errors ?? []

  return new ApiServiceError({
    status,
    errorType: body.errorType ?? defaultErrorType(status),
    message:
      body.error ??
      (errors.length > 0 ? errors.join(', ') : (STATUS_MESSAGES[status] ?? DEFAULT_MESSAGE)),
    errors,
    data,
  })
}

const ApiService = axios.create({
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  transformRequest: [
    transformRequest,
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
  ],
  transformResponse: [
    ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
    transformResponse,
  ],
  // Rails only permits arrays sent as key[]=a&key[]=b; qs's default indices format becomes a hash.
  paramsSerializer: (params) => stringify(decamelizeKeys(params), { arrayFormat: 'brackets' }),
})

ApiService.interceptors.response.use(
  (response) => {
    installCsrfToken(response.headers)
    return response
  },
  (error: unknown) => {
    const url = isAxiosError(error) ? error.config?.url : undefined
    if (isAxiosError(error)) installCsrfToken(error.response?.headers)

    const apiError = toApiServiceError(error)
    if (apiError.status === 401 && !(url && SESSION_ENDPOINT.test(url))) {
      unauthorizedHandlers.forEach((handler) => handler(apiError))
    }
    return Promise.reject(apiError)
  },
)

export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler)
  return () => {
    unauthorizedHandlers.delete(handler)
  }
}

// Multipart callers write snake_case field names themselves; FormData is never re-keyed.
export function postForm<T>(
  url: string,
  formData: FormData,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return ApiService.post<T>(url, formData, config)
}

export function patchForm<T>(
  url: string,
  formData: FormData,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return ApiService.patch<T>(url, formData, config)
}

export default ApiService
